import React, { useState, useEffect, useCallback } from 'react';
import { Button, Spin } from 'antd';
import SparkleCTASection from '../../components/CTASection';
import { SendOutlined, LoadingOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb, faMagnifyingGlass, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { useMediaQuery } from 'react-responsive';
import { ROUTE_PATH } from '../../../../../constants/routePath';
import RatingsSection from '../../components/RatingsSection';
import FAQ from '../../../../../components/StyledFAQ';
import {
    StyledLayout,
    StyledContent,
    StyledRow,
    StyledTitle,
    StyledSubtitle,
    StyledGradientSection,
    StyledInputContainer,
    SectionContent,
    StyledCol,
    StyledStepSection,
    StyledStepIcon,
    StyledStepContent,
    StyledNameButton,
    StyledNameGrid,
    StyledInput,
} from './styled';
import SEO from '../../../../../components/SEO';
import userService from '../../../../../services/tools/home';
import Footer from '../../../../../layouts/Footer';
import Header from "../../../../../layouts/Header";

const DomainNameGenerator = () => {
    const [inputValue, setInputValue] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isInputValid, setIsInputValid] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const navigate = useNavigate();

    useEffect(() => {
        setIsInputValid(inputValue.trim().length > 0 && inputValue.length <= 100);
    }, [inputValue]);

    const handleSearch = useCallback(async () => {
        if (!isInputValid) return;
        setIsLoading(true);
        setErrorMessage(''); // Clear any previous error messages
        try {
            const response = await userService.checkDomainAvailability(inputValue);
            setSearchResults(response.suggestions);
        } catch (error) {
            console.error('Error fetching domain data:', error);
            setErrorMessage('Failed to fetch domain suggestions. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [inputValue, isInputValid]);

    const handleTryMoreNames = () => {
        // Implement additional name generation logic here if needed
    };

    const handleStartOver = () => {
        setInputValue('');
        setSearchResults([]);
    };

    const handleNameClick = useCallback(
        debounce((domain) => {
            window.location.href = `https://admin.sparkle.store${ROUTE_PATH.REGISTER}?domain=${domain}`;
        }, 1000, { leading: true, trailing: false }),
        [navigate]
    );

    const sparkleDomainNameGeneratorFAQ = [
        {
            question: "What is Sparkle's Free Domain Name Generator & Ideas?",
            answer: `Sparkle's Free Domain Name Generator & Ideas is an innovative tool designed to help creators, entrepreneurs, and businesses find the perfect domain names for their websites. Here's what makes our generator unique:
      
          • Keyword-based suggestions: Enter relevant keywords, and our AI-powered generator provides unique domain name ideas aligned with your brand.
          • Instant availability check: We automatically verify domain availability, saving you time and frustration.
          • Versatility: Whether you're launching a blog, e-commerce store, or personal brand, our tool caters to all niches and industries.
          • SEO-friendly options: We prioritize domain suggestions that can boost your search engine rankings.
          • User-friendly interface: Our intuitive design makes finding the perfect domain name quick and easy.
      
          By using Sparkle's free domain name generator, you're taking the first step towards establishing a strong online presence. A well-chosen domain name can significantly impact your website's visibility, brand recognition, and overall success in the digital landscape.`
        },
        {
            question: "How does Sparkle's Free Domain Name Generator work?",
            answer: `Sparkle's Free Domain Name Generator utilizes advanced algorithms to create unique, brandable domain names. Here's a step-by-step breakdown of how it works:
      
          1. Keyword Input: Enter a word or phrase related to your brand, industry, or niche.
          2. Algorithm Processing: Our AI analyzes your input and generates a list of creative domain name ideas.
          3. Availability Check: We instantly verify the availability of each suggested domain name.
          4. Results Display: You receive a curated list of available, SEO-friendly domain names.
          5. Refinement: You can tweak your input or use filters to get more tailored results.
      
          Our generator considers factors like brandability, memorability, and SEO potential when creating suggestions. It also offers various domain extensions (.com, .net, .org, etc.) to give you more options.
      
          Pro tip: Try different combinations of keywords to explore a wide range of possibilities. Remember, the perfect domain name balances your brand identity with SEO best practices.`
        },
        {
            question: "Is Sparkle's Domain Name Generator really free?",
            answer: `Yes, Sparkle's Domain Name Generator is 100% free to use. We believe in empowering creators and businesses by providing accessible tools for online success. Here's what you get at no cost:
      
          • Unlimited domain name generation
          • Instant availability checks
          • Access to a wide range of domain extensions
          • SEO-friendly domain suggestions
          • User-friendly interface
      
          While the generator itself is free, please note that registering a domain name with a registrar will involve a fee. However, our tool helps you find the perfect domain name without any upfront cost or commitment.
      
          We maintain this free service as part of our commitment to fostering online entrepreneurship and creativity. By providing this valuable tool at no cost, we aim to lower the barriers to entry for individuals and businesses looking to establish their online presence.`
        },
        {
            question: "How can I choose the best domain name with Sparkle's generator?",
            answer: `Selecting the best domain name is crucial for your online success. Here are some expert tips to help you choose the perfect domain name using Sparkle's generator:
      
          1. Keep it short and memorable: Aim for 2-3 words maximum. E.g., "BrightIdeas.com" is better than "MyBrightIdeasWebsite.com".
          2. Use relevant keywords: Include terms related to your niche for better SEO. For a cooking blog, "GourmetRecipes.com" incorporates a relevant keyword.
          3. Avoid numbers and hyphens: They can make your domain harder to remember and type. Choose "ColorfulArt.com" over "Colorful-Art-2.com".
          4. Consider brandability: Choose a name that can grow with your business. "Amazon.com" is more brandable than "BooksOnline.com".
          5. Check for trademark issues: Ensure your chosen name doesn't infringe on existing trademarks.
          6. Test for readability: When words run together, ensure they don't spell something unintended. "ExpertsExchange.com" could be misread.
          7. Use popular TLDs: While .com is most recognized, don't rule out .net, .org, or niche-specific extensions like .tech or .design.
      
          Remember, a good domain name is an investment in your brand's future. Take your time to find one that aligns with your vision and appeals to your target audience.`
        },
        {
            question: "What factors should I consider when selecting a domain name with Sparkle?",
            answer: `When using Sparkle's Domain Name Generator, consider these critical factors to select a domain name that will benefit your online presence:
      
          1. SEO Impact: 
             • Include relevant keywords to improve search rankings
             • Avoid keyword stuffing, which can penalize your site
             • Example: For a local bakery, "FreshBakedBread.com" includes relevant keywords
      
          2. Brand Alignment: 
             • Choose a name that reflects your brand's personality and values
             • Ensure it's consistent with your overall branding strategy
             • Example: "EcoFriendlyLiving.com" clearly communicates an environmental focus
      
          3. Domain Extension: 
             • .com remains the most recognized, but consider alternatives
             • Industry-specific extensions can be beneficial (e.g., .tech for technology companies)
             • Example: A non-profit might opt for "OceanConservation.org"
      
          4. Future-Proofing: 
             • Select a name that allows for business growth and expansion
             • Avoid names that might limit your future offerings
             • Example: "DigitalSolutions.com" is broader than "EmailMarketingTool.com"
      
          5. Target Audience Appeal: 
             • Consider your audience's preferences and expectations
             • Use language and tone that resonates with your target market
             • Example: For a youth-oriented brand, "TrendyStyles.com" might be appropriate
      
          6. Competitive Advantage: 
             • Research competitor domains to ensure yours stands out
             • Look for opportunities to differentiate your brand
             • Example: If competitors use generic terms, a unique brand name like "Zesty.com" could stand out
      
          Remember, your domain name is often the first interaction potential customers have with your brand. It's worth investing time to find one that ticks all these boxes and sets you up for long-term online success.`
        },
        {
            question: "Can Sparkle's Domain Name Generator help with niche-specific domains?",
            answer: `Absolutely! Sparkle's Domain Name Generator is highly effective for creating niche-specific domains. Our advanced algorithms are designed to cater to a wide range of industries and specialties. Here's how it excels in generating niche-specific domains:
      
          1. Keyword Analysis: Our generator analyzes niche-specific keywords to create relevant domain suggestions. For example, entering "vegan recipes" might generate options like "PlantBasedDelights.com" or "VeganCuisineHub.com".
      
          2. Industry-Specific Extensions: We offer relevant TLDs for different niches. A tech startup might consider .tech or .io extensions, while a photographer could opt for .photography.
      
          3. Trend Awareness: Our system stays updated with industry trends, incorporating popular terms and concepts into domain suggestions.
      
          4. Brandability Within Niches: We balance niche-specific terms with brandable elements to create unique, memorable domains.
      
          5. Local SEO Considerations: For location-based businesses, our generator can incorporate geographical terms to boost local SEO.
      
          Examples of niche-specific domains our generator might produce:
      
          • Fitness niche: "FlexFitnessGear.com" or "YogaFlowMaster.com"
          • Eco-friendly products: "GreenLivingSolutions.eco" or "SustainableChoices.com"
          • Gourmet cooking: "GourmetPantrySecrets.com" or "CulinaryArtistry.chef"
          • Tech startups: "InnovateAI.tech" or "CloudSolutionsHub.io"
          • Pet care: "PawsomePetCare.com" or "FurryFriendEssentials.pet"
      
          By leveraging Sparkle's Domain Name Generator for niche-specific domains, you're more likely to find a domain that resonates with your target audience and improves your visibility within your specific market segment.`
        },
        {
            question: "Why is having a strong domain name important for online success?",
            answer: `A strong, well-chosen domain name is fundamental to online success. It serves as the foundation of your digital presence and can significantly impact various aspects of your online business. Here's why a strong domain name is crucial:
      
          1. SEO Benefits:
             • Improved search engine rankings when including relevant keywords
             • Higher click-through rates from search results
             • Example: "BestOrganicCoffee.com" may rank well for organic coffee-related searches
      
          2. Brand Recognition:
             • Easier for customers to remember and recall
             • Reinforces your brand identity
             • Case Study: Amazon.com - a short, unique name that's become synonymous with online shopping
      
          3. Credibility and Trust:
             • A professional domain name enhances your business's perceived legitimacy
             • Customers are more likely to trust a business with a strong, relevant domain
             • Example: "SecureBankingOnline.com" instills more confidence than "CheapBankStuff.net"
      
          4. Marketing and Advertising:
             • Easier to include in marketing materials and advertisements
             • More effective in word-of-mouth marketing
             • Example: Nike's "JustDoIt.com" aligns perfectly with their slogan and is easy to share
      
          5. First Impressions:
             • Often the first interaction a potential customer has with your brand
             • Sets expectations for your website and business
             • Example: "LuxuryWatches.com" immediately conveys the nature of the business
      
          6. Competitive Advantage:
             • Stands out from competitors in a crowded market
             • Can become a valuable business asset
             • Case Study: Cars.com sold for $872 million in 2017, partly due to its premium domain name
      
          7. Long-term Value:
             • A strong domain appreciates over time as your brand grows
             • Protects your brand from potential competitors or imitators
             • Example: Booking.com has become one of the most valuable domains in the travel industry
      
          8. Social Media Integration:
             • Easier to maintain consistent branding across platforms
             • Improves discoverability on social media
             • Example: Fashion brand ASOS uses "asos" consistently across all platforms
      
          By using Sparkle's Domain Name Generator, you're taking a crucial step towards securing a domain name that can provide these benefits and set your online venture up for long-term success. Remember, your domain name is more than just a web address - it's a powerful tool for building your brand and driving your online success.`
        }
    ];

    const seoData = {
        title: `Free Domain Name Generator | Find Your Perfect Domain in Seconds (${new Date().getFullYear()})`,
        description: "Generate creative and unique domain names with Sparkle's free AI-powered Domain Name Generator. Find the perfect domain for your creator brand or online business instantly.",
        canonicalUrl: "https://www.sparkle.store/tools/domain-name-generator",
        structuredData: {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Sparkle Domain Name Generator",
            "description": "AI-powered tool to generate unique and creative domain names that will help creators and online businesses establish their brand and grow their online presence",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
            },
            "featureList": [
                "Free to use",
                "AI-powered suggestions",
                "Instant domain availability check",
                "Multiple domain extension options",
                "Creator-focused domain ideas"
            ]
        }
    };

    return (
        <StyledLayout>
            <SEO
                title={seoData.title}
                description={seoData.description}
                canonicalUrl={seoData.canonicalUrl}
                structuredData={seoData.structuredData}
            />
            <Header/>
            <StyledContent>
                <StyledGradientSection>
                    <SectionContent>
                        <StyledSubtitle>FREE TOOLS</StyledSubtitle>
                        <StyledTitle level={1}>Free Domain Name Generator & Ideas ({new Date().getFullYear()})</StyledTitle>
                        <StyledSubtitle>
                            Search for the best domain name ideas and instantly verify domain availability
                        </StyledSubtitle>
                        <StyledInputContainer>
                            <StyledInput
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                placeholder="Search for a domain"
                                style={{ width: '100%' }}
                                maxLength={100}
                            />
                            {!isMobile && (
                                <Button
                                    icon={<SendOutlined />}
                                    type="primary"
                                    onClick={handleSearch}
                                    disabled={!isInputValid || isLoading}
                                    style={{
                                        height: '60px',
                                        width: '60px',
                                        marginLeft: '5px',
                                        backgroundColor: '#ffe955',
                                        color: 'black'
                                    }}
                                />
                            )}
                        </StyledInputContainer>
                        {isMobile && (
                            <Button
                                icon={<SendOutlined />}
                                type="primary"
                                onClick={handleSearch}
                                disabled={!isInputValid || isLoading}
                                style={{
                                    height: '60px',
                                    width: '100%',
                                    marginTop: '10px',
                                    backgroundColor: '#ffe955',
                                    color: 'black'
                                }}
                            >
                                Generate Names
                            </Button>
                        )}
                        {isLoading ? (
                            <div style={{ marginTop: '20px', textAlign: 'center' }}>
                                <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: '#ffe955' }} spin />} />
                                <p style={{ color: '#ffe955', marginTop: '10px' }}>Checking domain availability...</p>
                            </div>
                        ) : errorMessage ? (
                            <div style={{ marginTop: '20px', textAlign: 'center', color: 'red' }}>
                                <p>{errorMessage}</p>
                            </div>
                        ) : searchResults.length > 0 && (
                            <>
                                <div style={{ margin: '30px', alignSelf: 'flex-start', color: 'white', fontSize: '22px' }}>
                                    Here are some domain options for you:
                                </div>
                                <StyledNameGrid>
                                    {searchResults.map((result, index) => (
                                        <StyledNameButton
                                            key={index}
                                            onClick={() => handleNameClick(result)}
                                        >
                                            <span className="name">{result}</span>
                                        </StyledNameButton>
                                    ))}
                                </StyledNameGrid>
                                <StyledSubtitle style={{ marginTop: '20px' }}>Still not sure?</StyledSubtitle>
                                <Button
                                    type="primary"
                                    onClick={handleTryMoreNames}
                                    disabled={!isInputValid || isLoading}
                                    style={{
                                        height: '60px',
                                        width: 'auto',
                                        marginTop: '10px',
                                        backgroundColor: '#ffe955',
                                        color: 'black',
                                        borderRadius: '40px',
                                        fontWeight: '700'
                                    }}
                                >
                                    Let&apos;s try some more names
                                </Button>
                                <div style={{ marginTop: '20px' }}>
                                    <a href="#" onClick={handleStartOver} style={{ color: '#ffe955' }}>Let&apos;s start over</a>
                                </div>
                            </>
                        )}
                    </SectionContent>
                </StyledGradientSection>
                <StyledStepSection>
                    <SectionContent>
                        <StyledRow>
                            <StyledCol>
                                <StyledStepIcon>
                                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                                    <span>01</span>
                                </StyledStepIcon>
                                <StyledStepContent>
                                    <h3>Search</h3>
                                    <p>Use Sparkle&apos;s AI-powered business name generator to instantly search and find unique, brandable domain names for your online venture.</p>
                                </StyledStepContent>
                            </StyledCol>
                            <StyledCol>
                                <StyledStepIcon>
                                    <FontAwesomeIcon icon={faLightbulb} />
                                    <span>02</span>
                                </StyledStepIcon>
                                <StyledStepContent>
                                    <h3>Select</h3>
                                    <p>Secure your ideal domain name that perfectly aligns with your brand identity and business goals before it&apos;s taken by competitors.</p>
                                </StyledStepContent>
                            </StyledCol>
                            <StyledCol>
                                <StyledStepIcon>
                                    <FontAwesomeIcon icon={faCheck} />
                                    <span>03</span>
                                </StyledStepIcon>
                                <StyledStepContent>
                                    <h3>Sell</h3>
                                    <p>Begin your complimentary trial period with Sparkle. Effortlessly upload your products and access all the essential tools to kickstart your e-commerce success.</p>
                                </StyledStepContent>
                            </StyledCol>
                        </StyledRow>
                    </SectionContent>
                </StyledStepSection>
                <RatingsSection />
                <SectionContent style={{ marginBottom: '80px' }}>
                    <FAQ title="Frequently Asked Questions" faqItems={sparkleDomainNameGeneratorFAQ} />
                </SectionContent>
                <SparkleCTASection />
            </StyledContent>
            <Footer/>
        </StyledLayout>
    );
};

export default DomainNameGenerator;
